import React, { useEffect, useState } from "react";
import type { QuickProofChange, QuickProofDirection, QuickProofView } from "@siteagain/shared-types";

import { operatorFetch } from "./operator/format";

/**
 * The quick proof (ADR-0031), shown in seconds. As-is is the owner's own page
 * served without script, with what can be fixed from its own content fixed.
 * The improvement directions are the same page's facts drawn by the generic
 * renderer. None of them is the migration: the full build happens after a plan
 * is chosen and is the only thing that can go live.
 */

const POLL_MS = 1_500;

const COPY: Record<QuickProofDirection, { working: string; ready: string; frame: string }> = {
  "as-is": {
    working: "Reading your homepage as a visitor sees it.",
    ready: "Your page, without the weight it carries today.",
    frame: "quick proof",
  },
  conversion: {
    working: "Reordering your homepage around the next step.",
    ready: "Your page, built to turn a visit into an enquiry.",
    frame: "conversion proof",
  },
  redesign: {
    working: "Drawing your homepage in a new visual direction.",
    ready: "Your words, in a new look.",
    frame: "new look proof",
  },
};

export function JourneyQuickProof({
  siteId,
  proofId,
  direction,
  sourceHost,
  onNext,
  onChangeDirection,
  onRetry,
}: {
  siteId: string;
  proofId: string;
  direction: QuickProofDirection;
  sourceHost: string;
  /** The plan comes after the proof. */
  onNext(): void;
  onChangeDirection(): void;
  onRetry(): void;
}) {
  const [proof, setProof] = useState<QuickProofView | null>(null);
  const [error, setError] = useState("");
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    async function poll() {
      try {
        const body = await operatorFetch<{ proof: QuickProofView }>(`/sites/${siteId}/proofs/${proofId}`);
        if (cancelled) return;
        setProof(body.proof);
        setError("");
        if (body.proof.status === "running") timer = window.setTimeout(poll, POLL_MS);
      } catch (reason) {
        if (cancelled) return;
        setError(reason instanceof Error ? reason.message : "The proof could not be loaded");
        timer = window.setTimeout(poll, POLL_MS * 2);
      }
    }
    setProof(null);
    void poll();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [siteId, proofId]);

  useEffect(() => {
    if (proof?.status === "ready" || proof?.status === "failed") return;
    const ticker = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(ticker);
  }, [proof?.status]);

  if (!proof || proof.status === "running") {
    const elapsed = proof ? Math.max(0, Math.round((now - Date.parse(proof.createdAt)) / 1000)) : null;
    return (
      <div className="jr-wrap wide jr-preview-wait">
        <div className="jr-preview-wait-head">
          <span className="jr-eyebrow"><i className="pulse" />Quick proof in progress · {sourceHost}</span>
          <h1 className="jr-h1">{COPY[direction].working}</h1>
          <p className="jr-lede">
            We open your page once and work only from what it already says: nothing is invented, and your live site stays untouched.
          </p>
          <div className="jr-row">
            <span className="jr-chip ink">{elapsed === null ? "Starting" : `${elapsed} s`}</span>
            {error && <span className="jr-chip">{error}</span>}
          </div>
        </div>
      </div>
    );
  }

  if (proof.status === "failed" || !proof.result || !proof.pagePath) {
    return (
      <div className="jr-wrap wide jr-preview-wait">
        <div className="jr-preview-wait-head">
          <span className="jr-eyebrow">Quick proof · {sourceHost}</span>
          <h1 className="jr-h1">We could not open your homepage.</h1>
          <p className="jr-lede">{proof.error ?? "The page did not finish loading in time."} Nothing was changed on your site.</p>
          <div className="jr-row">
            <button className="jr-btn" type="button" onClick={onRetry}>Try again</button>
            <button className="jr-btn ghost" type="button" onClick={onChangeDirection}>Choose another direction</button>
          </div>
        </div>
      </div>
    );
  }

  const result = proof.result;
  const applied = result.changes.filter((change) => change.status === "applied");
  const deferred = result.changes.filter((change) => change.status === "full_build");
  const seconds = (result.timings.totalMs / 1000).toFixed(1);

  return (
    <div className="jr-wrap wide">
      <div className="jr-preview-head">
        <div>
          <div className="jr-row">
            <span className="jr-chip ok">Quick proof ready in {seconds} s</span>
            <span className="jr-chip">Private · only you can see it</span>
          </div>
          <h1 className="jr-h1">{COPY[direction].ready}</h1>
        </div>
      </div>

      <div className="jr-split preview">
        <main className="jr-main">
          <div className="jr-frame">
            <div className="jr-frame-bar">
              <div className="jr-dots"><i /><i /><i /></div>
              <div className="jr-url">{sourceHost} · {COPY[direction].frame}</div>
              <a className="jr-chip outline" href={`/api${proof.pagePath}`} target="_blank" rel="noreferrer">Open full screen ↗</a>
            </div>
            <div className="jr-frame-body">
              {/* The proof is served with a CSP sandbox; the attribute repeats it so the page never runs in this origin. */}
              <iframe src={`/api${proof.pagePath}`} title={`${sourceHost} quick proof`} sandbox="" referrerPolicy="no-referrer" />
            </div>
          </div>

          <div className="jr-section-head" style={{ marginTop: 28 }}>
            <h2>{direction === "as-is" ? "What changed that you cannot see" : "What changed, and why"}</h2>
          </div>
          <div className="jr-proof-grid">
            <div className="jr-card jr-proof"><small>Done in this proof</small><strong>{applied.length}</strong><span>improvement{applied.length === 1 ? "" : "s"}</span></div>
            <div className="jr-card jr-proof"><small>For the full build</small><strong>{deferred.length}</strong><span>item{deferred.length === 1 ? "" : "s"} need a build or your input</span></div>
            {result.pageFormat === "page_spec" ? (
              <div className="jr-card jr-proof"><small>Headline</small><strong>{result.changes.some((change) => change.kind === "headline") ? "Rewritten" : "Yours"}</strong><span>{result.changes.some((change) => change.kind === "headline") ? "from facts already on your page" : "kept word for word"}</span></div>
            ) : (
              <div className="jr-card jr-proof"><small>Page weight</small><strong>{formatBytes(result.bytes.proof)}</strong><span>HTML, was {formatBytes(result.bytes.source)}</span></div>
            )}
            <div className="jr-card jr-proof"><small>Built in</small><strong>{seconds} s</strong><span>one render, no build</span></div>
          </div>
          <ChangeList title="Done in this proof" changes={applied} />
          {deferred.length > 0 && <ChangeList title="Found, and handled in the full build" changes={deferred} />}

          <section className="jr-card jr-kept" style={{ marginTop: 20 }}>
            <h3>What an AI assistant reads from this page</h3>
            <pre className="jr-proof-summary">{result.aiSummary}</pre>
          </section>
        </main>

        <aside className="jr-aside">
          <section className="jr-card jr-next">
            <span className="jr-eyebrow">Your next step</span>
            <h2>Like it? Choose a plan.</h2>
            <p className="jr-muted" style={{ margin: 0, fontSize: 14 }}>
              After you choose, we build the real site: every page, checked for SEO, speed and visual match before anything can go live.
            </p>
            <button className="jr-btn wide" type="button" onClick={onNext}>Choose a plan</button>
            <button className="jr-btn ghost wide" type="button" onClick={onChangeDirection}>Try the other direction</button>
          </section>
          <section className="jr-card jr-kept">
            <h3>What this proof is not</h3>
            <ul>{result.limitations.map((limitation) => <li key={limitation}>{limitation}</li>)}</ul>
          </section>
        </aside>
      </div>
    </div>
  );
}

function ChangeList({ title, changes }: { title: string; changes: QuickProofChange[] }) {
  if (changes.length === 0) return null;
  return (
    <section className="jr-card jr-kept" style={{ marginTop: 20 }}>
      <h3>{title}</h3>
      <ul className="jr-proof-changes">
        {changes.map((change) => (
          <li key={`${change.kind}-${change.title}`}>
            <strong>{change.title}</strong>
            <span>{change.detail}</span>
            {(change.before || change.after) && (
              <small>{change.before ?? "—"}{change.after ? ` → ${change.after}` : ""}</small>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function formatBytes(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
